import { FC, useState, useEffect } from 'react';
import { InputAdornment } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { help } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ApplyDiscountHeader,
  DefaultLabel,
  StyledSwitch,
  DiscountContainer,
} from '../CreateOrder/Discounts.styles';
import {
  FormContainer,
  ScrollableContent,
  Container,
  LeftColumn,
  HeaderRow,
  LabelTypography,
  Spacer24,
  ItemCard,
  ItemRow,
  SelectBox,
  StyledAutocomplete,
  StyledAutocompleteTextField,
  ErrorTextSelect,
  AmountBox,
  StyledTextField,
  ErrorTextAmount,
  RightPanel,
  FinalTotalsTitle,
  TotalsRow,
  TotalLabel,
  TotalValue,
  SurchargeRow,
  GrandTotalBox,
  NextButtonContainer,
  BackButton,
  NextButton,
} from '../CreateOrder/Surcharges.styles';
import {
  EditOrderDiscountsProps,
  EditOrderDiscount,
  EditOrderItem,
} from 'src/models/EditOrder.interface';
import { getBasicDiscounts } from 'src/services/discount.service';
import { DiscountLabelRed } from '../CreateOrder/Summary.styles';

const Discounts: FC<EditOrderDiscountsProps> = ({
  onNext,
  onBack,
  orderId,
  orderData,
}) => {
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discounts, setDiscounts] = useState<EditOrderDiscount[]>([]);
  const [itemsSubtotal, setItemsSubtotal] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [discountsLoaded, setDiscountsLoaded] = useState(false);
  const [initialFormValues, setInitialFormValues] = useState({
    discountId: '',
    discountValue: '',
    applyDiscount: false,
  });
  const { showSnackbar } = StyledSnackbar();

  useEffect(() => {
    calculateItemsSubtotal();
  }, [orderId, orderData]);

  useEffect(() => {
    fetchDiscounts();
  }, []);

  useEffect(() => {
    if (discountsLoaded && discounts.length > 0 && !isInitialized) {
      initializeFormData();
    }
  }, [discountsLoaded, discounts.length, isInitialized]);

  const fetchDiscounts = async () => {
    try {
      const data = await getBasicDiscounts();
      const discountData = data;
      setDiscounts(discountData);
      setDiscountsLoaded(true);
    } catch (error: unknown) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      setDiscountsLoaded(true);
    }
  };

  const calculateItemsSubtotal = () => {
    const step2Data = localStorage.getItem(`editOrderStep2_${orderId}`);
    if (step2Data) {
      try {
        const { items } = JSON.parse(step2Data);
        const total = items.reduce(
          (sum: number, item: EditOrderItem) =>
            sum + item.quantity * Number(item.unitPrice),
          0
        );
        setItemsSubtotal(total);
        return;
      } catch (e) {
        // Fall through to orderData
      }
    }

    if (orderData && orderData.items) {
      const total = orderData.items.reduce(
        (sum: number, item) => sum + item.quantity * Number(item.unitPrice),
        0
      );
      setItemsSubtotal(total);
    }
  };

  const initializeFormData = () => {
    const savedData = localStorage.getItem(`editOrderStep3_${orderId}`);
    let useLocalStorage = false;
    let parsedSavedData: {
      applyDiscount: boolean;
      discountId: string | null;
      discountValue: string | null;
      discountAmount: number;
    } | null = null;

    if (savedData) {
      try {
        parsedSavedData = JSON.parse(savedData);

        if (parsedSavedData) {
          if (
            parsedSavedData.applyDiscount === true &&
            parsedSavedData.discountId &&
            parsedSavedData.discountId !== ''
          ) {
            const discountExists = discounts.some(
              (d) => String(d.id) === String(parsedSavedData!.discountId)
            );
            if (discountExists) {
              useLocalStorage = true;
            }
          } else if (parsedSavedData.applyDiscount === false) {
            useLocalStorage = true;
          }
        }
      } catch (e) {
        useLocalStorage = false;
      }
    }

    let newApplyDiscount = false;
    let newDiscountId = '';
    let newDiscountValue = '';

    if (useLocalStorage && parsedSavedData) {
      newApplyDiscount = !!parsedSavedData.applyDiscount;
      newDiscountId = parsedSavedData.discountId || '';
      newDiscountValue = parsedSavedData.discountValue || '';

      if (!newDiscountValue && newDiscountId) {
        const sd = discounts.find((d) => String(d.id) === newDiscountId);
        if (sd) {
          newDiscountValue = sd.percentage
            ? String(sd.percentage)
            : String(sd.defaultValue ?? '');
        }
      }
    } else if (orderData?.discount) {
      newApplyDiscount = true;

      const orderDiscountId = orderData.discount.discountId;
      const orderDiscountName = orderData.discount.name?.trim().toLowerCase();

      let matchedDiscount = discounts.find(
        (d) => String(d.id) === String(orderDiscountId)
      );

      if (!matchedDiscount && orderDiscountName) {
        matchedDiscount = discounts.find(
          (d) => d.name?.trim().toLowerCase() === orderDiscountName
        );
      }

      if (matchedDiscount) {
        newDiscountId = matchedDiscount.id;
        newDiscountValue = String(
          orderData.discount.percentage ??
            orderData.discount.amount ??
            matchedDiscount.percentage ??
            matchedDiscount.defaultValue ??
            ''
        );
      } else {
        newDiscountValue = String(
          orderData.discount.percentage ?? orderData.discount.amount ?? ''
        );
      }

      const dataToSave = {
        applyDiscount: newApplyDiscount,
        discountId: newDiscountId,
        discountValue: newDiscountValue,
        discountAmount: 0,
        discountName:
          newApplyDiscount && orderData.discount ? orderData.discount.name : '',
        discountPercentage:
          newApplyDiscount &&
          orderData.discount &&
          orderData.discount.percentage
            ? Number(orderData.discount.percentage)
            : null,
        discounts: discounts,
      };
      localStorage.setItem(
        `editOrderStep3_${orderId}`,
        JSON.stringify(dataToSave)
      );
    }

    setApplyDiscount(newApplyDiscount);
    setInitialFormValues({
      discountId: newDiscountId,
      discountValue: newDiscountValue,
      applyDiscount: newApplyDiscount,
    });

    setIsInitialized(true);
  };

  const validationSchema = Yup.object().shape({
    discountId: Yup.string().when('applyDiscount', {
      is: true,
      then: (schema) => schema.required(help.FIELD_REQUIRED),
      otherwise: (schema) => schema,
    }),
    discountValue: Yup.number().when('applyDiscount', {
      is: true,
      then: (schema) =>
        schema
          .min(0, help.MIN_VALUE_REQUIRED)
          .test(
            'max-percentage',
            'Percentage cannot exceed 100',
            function (value) {
              const { discountId } = this.parent;
              const selectedDiscount = discounts.find(
                (d) => d.id === discountId
              );
              if (selectedDiscount?.percentage && value != null) {
                return value <= 100;
              }
              return true;
            }
          )
          .test(
            'max-amount',
            'Discount cannot exceed subtotal',
            function (value) {
              const { discountId } = this.parent;
              const selectedDiscount = discounts.find(
                (d) => d.id === discountId
              );
              if (
                selectedDiscount &&
                !selectedDiscount.percentage &&
                value != null
              ) {
                return value <= itemsSubtotal;
              }
              return true;
            }
          )
          .required(help.VALUE_REQUIRED),
      otherwise: (schema) => schema,
    }),
  });

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      const selectedDisc = discounts.find((d) => d.id === values.discountId);
      let calcDiscountAmount = 0;

      if (applyDiscount && selectedDisc) {
        const formValue =
          values.discountValue !== '' && values.discountValue !== undefined
            ? Number(values.discountValue)
            : NaN;
        const rawValue = !isNaN(formValue)
          ? formValue
          : selectedDisc.percentage
          ? Number(selectedDisc.percentage)
          : selectedDisc.defaultValue
          ? Number(selectedDisc.defaultValue)
          : 0;
        if (selectedDisc.percentage) {
          calcDiscountAmount = (itemsSubtotal * rawValue) / 100;
        } else {
          calcDiscountAmount = rawValue;
        }
      }

      const dataToSave = {
        applyDiscount,
        discountId: applyDiscount ? values.discountId : null,
        discountValue: applyDiscount ? values.discountValue : null,
        discountAmount: applyDiscount ? calcDiscountAmount : 0,
        discountName:
          applyDiscount && selectedDiscount ? selectedDiscount.name : '',
        discountPercentage:
          applyDiscount && selectedDiscount && selectedDiscount.percentage
            ? values.discountValue !== '' && values.discountValue !== undefined
              ? Number(values.discountValue)
              : Number(selectedDiscount.percentage)
            : null,
        discounts: discounts,
      };
      localStorage.setItem(
        `editOrderStep3_${orderId}`,
        JSON.stringify(dataToSave)
      );
      onNext();
    },
  });

  const selectedDiscount = discounts.find(
    (d) => d.id === formik.values.discountId
  );

  useEffect(() => {
    formik.validateForm();
  }, [applyDiscount]);

  useEffect(() => {
    if (!isInitialized) return;

    if (applyDiscount && !formik.values.discountId) return;

    const selectedDiscountForSave = discounts.find(
      (d) => d.id === formik.values.discountId
    );

    const dataToSave = {
      applyDiscount,
      discountId: applyDiscount ? formik.values.discountId : null,
      discountValue: applyDiscount ? formik.values.discountValue : null,
      discountAmount: 0,
      discountName:
        applyDiscount && selectedDiscountForSave
          ? selectedDiscountForSave.name
          : '',
      discountPercentage:
        applyDiscount &&
        selectedDiscountForSave &&
        selectedDiscountForSave.percentage
          ? formik.values.discountValue !== '' &&
            formik.values.discountValue !== undefined
            ? Number(formik.values.discountValue)
            : Number(selectedDiscountForSave.percentage)
          : null,
      discounts: discounts,
    };

    try {
      localStorage.setItem(
        `editOrderStep3_${orderId}`,
        JSON.stringify(dataToSave)
      );
    } catch (err) {
      // ignore storage errors
    }
  }, [
    applyDiscount,
    formik.values.discountId,
    formik.values.discountValue,
    isInitialized,
    orderId,
  ]);

  const calculateDiscountAmount = () => {
    if (!applyDiscount || !selectedDiscount) return 0;

    const formValue =
      formik.values.discountValue !== '' &&
      formik.values.discountValue !== undefined
        ? Number(formik.values.discountValue)
        : NaN;

    const rawValue = !isNaN(formValue)
      ? formValue
      : selectedDiscount.percentage
      ? Number(selectedDiscount.percentage)
      : selectedDiscount.defaultValue
      ? Number(selectedDiscount.defaultValue)
      : 0;

    if (selectedDiscount.percentage) {
      return (itemsSubtotal * rawValue) / 100;
    }
    return rawValue;
  };

  const discountAmount = calculateDiscountAmount();
  const netTotal = itemsSubtotal - discountAmount;

  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setApplyDiscount(checked);
    formik.setFieldValue('applyDiscount', checked);

    if (!checked) {
      formik.setFieldValue('discountId', '');
      formik.setFieldValue('discountValue', '');
      formik.setFieldError('discountId', '');
      formik.setFieldError('discountValue', '');
    } else {
      formik.validateField('discountId');
    }
  };

  return (
    <FormContainer onSubmit={formik.handleSubmit}>
      <ScrollableContent>
        <Container>
          <LeftColumn>
            <ApplyDiscountHeader>
              <DefaultLabel>Apply Discount</DefaultLabel>
              <StyledSwitch
                checked={applyDiscount}
                onChange={handleToggleChange}
              />
            </ApplyDiscountHeader>
            {applyDiscount && (
              <DiscountContainer>
                <HeaderRow>
                  <LabelTypography>Discount</LabelTypography>
                  <LabelTypography>Amount/Percentage</LabelTypography>
                  <Spacer24 />
                </HeaderRow>
                <ItemCard>
                  <ItemRow>
                    <SelectBox>
                      <StyledAutocomplete
                        options={discounts}
                        getOptionLabel={(option) => option.name}
                        value={
                          discounts.find(
                            (d) => d.id === formik.values.discountId
                          ) || null
                        }
                        onChange={(event, newValue) => {
                          const id = newValue?.id || '';
                          formik.setFieldValue('discountId', id);
                          if (newValue) {
                            formik.setFieldValue(
                              'discountValue',
                              newValue.percentage
                                ? newValue.percentage
                                : newValue.defaultValue || ''
                            );
                          } else {
                            formik.setFieldValue('discountValue', '');
                          }
                        }}
                        onBlur={formik.handleBlur}
                        ListboxProps={{
                          style: {
                            maxHeight: '350px',
                            overflow: 'auto',
                          },
                        }}
                        renderInput={(params) => (
                          <StyledAutocompleteTextField
                            {...params}
                            name="discountId"
                            placeholder="Select discount"
                            hasValue={!!formik.values.discountId}
                            hasError={
                              formik.touched.discountId &&
                              !!formik.errors.discountId
                            }
                          />
                        )}
                      />
                      {(formik.touched.discountId || formik.submitCount > 0) &&
                        formik.errors.discountId && (
                          <ErrorTextSelect>
                            {formik.errors.discountId}
                          </ErrorTextSelect>
                        )}
                    </SelectBox>
                    <AmountBox>
                      <StyledTextField
                        name="discountValue"
                        type="text"
                        value={formik.values.discountValue || ''}
                        onChange={(e) => {
                          const value = e.target.value.replace(/,/g, '');
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            const val = Number(value);
                            if (selectedDiscount?.percentage && val > 100) {
                              formik.setFieldError(
                                'discountValue',
                                'Percentage cannot exceed 100'
                              );
                              return;
                            }
                            if (
                              selectedDiscount &&
                              !selectedDiscount.percentage &&
                              val > itemsSubtotal
                            ) {
                              formik.setFieldError(
                                'discountValue',
                                'Discount amount cannot exceed items subtotal'
                              );
                              formik.setFieldValue(
                                'discountValue',
                                value === '' ? 0 : value
                              );
                              return;
                            }
                            formik.setFieldError('discountValue', undefined);
                            formik.setFieldValue(
                              'discountValue',
                              value === '' ? 0 : value
                            );
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={
                          (formik.touched.discountValue ||
                            formik.submitCount > 0) &&
                          Boolean(formik.errors.discountValue)
                        }
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedDiscount &&
                              selectedDiscount.percentage ? (
                                '%'
                              ) : (
                                <i className="bx bx-money" aria-hidden="true" />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      {(formik.touched.discountValue ||
                        formik.submitCount > 0) &&
                        formik.errors.discountValue && (
                          <ErrorTextAmount>
                            {formik.errors.discountValue}
                          </ErrorTextAmount>
                        )}
                    </AmountBox>
                  </ItemRow>
                </ItemCard>
              </DiscountContainer>
            )}
          </LeftColumn>
          <RightPanel>
            <FinalTotalsTitle>Totals Summary</FinalTotalsTitle>
            <TotalsRow>
              <TotalLabel>Items subtotal</TotalLabel>
              <TotalValue>
                {itemsSubtotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </TotalsRow>
            <SurchargeRow>
              <DiscountLabelRed>Discount</DiscountLabelRed>
              <DiscountLabelRed>
                {discountAmount > 0 ? '-' : ''}{' '}
                {discountAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </DiscountLabelRed>
            </SurchargeRow>
            <GrandTotalBox>
              <TotalLabel>Net Total</TotalLabel>
              <TotalValue>
                {netTotal.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </TotalValue>
            </GrandTotalBox>
          </RightPanel>
        </Container>
      </ScrollableContent>
      <NextButtonContainer>
        <BackButton onClick={onBack}>Back</BackButton>
        <NextButton type="submit">Next</NextButton>
      </NextButtonContainer>
    </FormContainer>
  );
};

export default Discounts;
